import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, Timer, User } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { formatRemaining, minutesRemaining } from '@/lib/sessionExtensions';

// Focused picker for binding an extension to the bay that is actually mid-session.
// Only bays with a running session are offered, so staff can never attach extra
// time to a rig nobody is sitting on.
export default function ExtensionBayPicker({ open, title, onSelect, onClose }) {
  const { data: bays = [], isLoading } = useQuery({
    queryKey: ['pos-live-bays'],
    queryFn: () => base44.entities.BookingRig.filter({ operational_status: 'LIVE' }, 'bay_number', 50),
    enabled: open,
    refetchInterval: open ? 10000 : false,
  });

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.96, y: 16 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.96, y: 16 }}
            transition={{ type: 'spring', stiffness: 240, damping: 22 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-2xl bg-[#111115] border border-white/10 overflow-hidden max-h-[90vh] flex flex-col"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div>
                <p className="text-primary text-[10px] font-bold tracking-widest">LIVE BAYS ONLY</p>
                <h3 className="text-white font-black text-lg uppercase">{title || 'Attach Extension to Active Bay'}</h3>
                <p className="text-white/40 text-xs mt-0.5">
                  Only bays currently running a session can take extra time.
                </p>
              </div>
              <button
                onClick={onClose}
                className="w-9 h-9 flex items-center justify-center bg-white/5 hover:bg-white/10 text-white/60 hover:text-white transition-colors shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 overflow-y-auto">
              {isLoading && (
                <p className="flex items-center gap-2 text-white/40 text-sm py-8 justify-center">
                  <Loader2 className="w-4 h-4 animate-spin" /> Checking which bays are running…
                </p>
              )}

              {!isLoading && bays.length === 0 && (
                <div className="text-center py-12">
                  <Timer className="w-9 h-9 text-white/10 mx-auto mb-3" />
                  <p className="text-white font-bold text-sm">No session is running on any bay</p>
                  <p className="text-white/40 text-xs mt-1">
                    An extension can only be sold while a rig is mid-session.
                  </p>
                </div>
              )}

              <div className="space-y-2.5">
                {bays.map((bay) => (
                  <button
                    key={bay.id}
                    onClick={() => onSelect(bay)}
                    className="w-full text-left border-2 border-white/10 hover:border-primary bg-[#0d0d0f] hover:bg-primary/[0.06] px-4 py-4 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <p className="text-white font-black text-base uppercase leading-tight truncate">
                          {bay.bay_number ? `${bay.bay_number} — ` : ''}
                          {bay.name}
                        </p>
                        <p className="flex items-center gap-1.5 text-white/50 text-xs mt-1 truncate">
                          <User className="w-3 h-3 shrink-0" />
                          {bay.current_driver || 'Driver not recorded'}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="flex items-center gap-1.5 text-primary font-black text-sm justify-end">
                          <Timer className="w-3.5 h-3.5" />
                          {formatRemaining(minutesRemaining(bay.session_expires_at))}
                        </p>
                        <p className="text-white/30 text-[10px] font-bold tracking-widest mt-1">TAP TO ATTACH</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}